<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useModalsStore } from "@/stores/modals";

const promptResponse = ref<string>("");
const isPromptConfirmButtonDisabled = ref<boolean>(false);
const expandableOpen = ref(false);

const modalsStore = useModalsStore();
const promptInput = useTemplateRef<InstanceType<typeof TextInput>>("promptInput");
const richTextEditor = useTemplateRef<InstanceType<typeof RichTextEditor>>("richTextEditor");

const modal = computed(() => modalsStore.getCurrentModal());
const modelBindings = computed(() => {
  const models =
    modal.value?.kind === "component"
      ? modal.value.models
      : modal.value?.kind === "builtin"
        ? modal.value.models
        : undefined;
  if (!models) {
    return {};
  }
  const bindings: Record<string, unknown> = {};
  for (const [key, model] of Object.entries(models)) {
    bindings[key] = model.value;
    bindings[`onUpdate:${key}`] = (value: unknown) => {
      model.value = value;
    };
  }
  return bindings;
});

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    cancel();
  } else if (
    event.key === "Enter" &&
    modal.value?.kind === "builtin" &&
    modal.value?.type !== "multiline-prompt"
  ) {
    event.preventDefault();
    event.stopPropagation();
    confirm();
  }
}
function cancel() {
  const m = modal.value;
  if (!m) return;

  if (m.kind === "component") {
    m.cancel();
  } else {
    m.onCancel();
  }
  promptResponse.value = "";
}
function confirm() {
  const m = modal.value;
  if (!m || m.kind === "component") return;

  if (m.type === "prompt" || m.type === "multiline-prompt") {
    m.response = promptResponse.value;
  }
  m.onConfirm();
  promptResponse.value = "";
}
function updatePromptConfirmButtonDisabledState() {
  isPromptConfirmButtonDisabled.value =
    !promptInput.value?.checkValidity() || promptResponse.value.trim().length === 0;
}
function toggleExpandable() {
  expandableOpen.value = !expandableOpen.value;
}

onMounted(() => {
  document.addEventListener("keydown", onKeyDown);
});
onUnmounted(() => {
  document.removeEventListener("keydown", onKeyDown);
});

watch(
  [promptResponse, modal],
  () => {
    updatePromptConfirmButtonDisabledState();
  },
  { flush: "post" }
);
watch(
  modal,
  (current) => {
    expandableOpen.value = false;
    if (
      current?.kind === "builtin" &&
      (current.type === "prompt" || current.type === "multiline-prompt")
    ) {
      promptResponse.value = current.initialValue ?? "";
    }
  },
  { immediate: true }
);
</script>

<template>
  <Transition name="modal-appear">
    <div v-if="modal" class="container">
      <div
        class="dialog shadow-lg"
        :class="{
          danger: 'isDangerous' in modal && modal.isDangerous,
          image: modal.kind === 'builtin' && modal.type === 'image',
        }"
      >
        <div class="header">
          <div class="text-base-medium">
            <i v-if="(modal.titleIcon ?? '').length > 0" class="icon" :class="modal.titleIcon" />
            {{ modal.title }}
          </div>
          <SimpleButton icon-left="icon-close" @click="cancel" kind="ghost" squared />
        </div>
        <div
          class="content"
          :class="[
            modal.kind,
            { 'content-expandable': modal.kind === 'builtin' && modal.type === 'expandable' },
          ]"
        >
          <template v-if="modal.kind === 'builtin'">
            <template v-if="modal.type === 'image'">
              <img :src="modal.imageSrc" :alt="modal.caption || modal.title" />

              <p v-if="modal.caption" class="caption text-sm-regular">{{ modal.caption }}</p>
            </template>

            <template v-else-if="modal.type === 'expandable'">
              <component
                v-if="modal.body"
                :is="modal.body"
                v-bind="{
                  ...(modal.bodyProps ?? {}),
                  ...modelBindings,
                }"
              />
              <div
                v-else-if="modal.message.length > 0"
                class="paragraph-s"
                v-html="modal.message"
              />

              <div v-if="modal.expandableBody" class="expandable-block">
                <Transition name="modal-expand">
                  <div v-if="expandableOpen" class="expand-inner">
                    <div class="expand-wrapper">
                      <component
                        :is="modal.expandableBody"
                        v-bind="modal.expandableBodyProps ?? {}"
                      />
                    </div>
                  </div>
                </Transition>
              </div>
            </template>

            <template v-else>
              <div v-if="modal.message.length > 0" class="paragraph-s" v-html="modal.message" />

              <label class="prompt text-base-medium" v-if="modal.type === 'prompt'">
                {{ modal.promptedValueName }}
                <TextInput
                  ref="promptInput"
                  v-model="promptResponse"
                  :focus="true"
                  @keydown.enter="confirm"
                  :type="modal.inputType"
                  :pattern="modal.inputPattern"
                />

                <span v-if="modal.error" class="prompt-error text-sm-regular">
                  {{ modal.error }}
                </span>
              </label>

              <label class="prompt text-base-medium" v-if="modal.type === 'multiline-prompt'">
                {{ modal.promptedValueName }}

                <div class="editor">
                  <div class="format-actions">
                    <div class="format" @click="richTextEditor?.markBold()">
                      <i class="icon icon-bold" />
                    </div>
                    <div class="format" @click="richTextEditor?.markItalic()">
                      <i class="icon icon-italic" />
                    </div>
                    <div class="format" @click="richTextEditor?.markList()">
                      <i class="icon icon-list" />
                    </div>
                  </div>
                  <RichTextEditor
                    ref="richTextEditor"
                    v-model:value="promptResponse"
                    :focus="true"
                  />
                </div>
              </label>
            </template>
          </template>

          <template v-else>
            <component
              :is="modal.body"
              v-bind="{
                ...(modal.bodyProps ?? {}),
                ...modelBindings,
                close: modal.close,
                cancel: modal.cancel,
              }"
            />
          </template>
        </div>
        <div class="actions alert" v-if="modal.kind === 'builtin' && modal.type === 'alert'">
          <SimpleButton @click="confirm" :label="modal.confirmText" kind="primary" />
        </div>

        <div
          class="actions expandable"
          v-if="modal.kind === 'builtin' && modal.type === 'expandable'"
        >
          <SimpleButton
            kind="ghost"
            icon-right="icon-chevron-up-down"
            :class="{ 'toggle-expanded': expandableOpen }"
            :label="expandableOpen ? modal.collapseLabel : modal.expandLabel"
            @click="toggleExpandable"
          />
        </div>

        <div class="actions confirm" v-if="modal.kind === 'builtin' && modal.type === 'confirm'">
          <SimpleButton
            v-if="modal.cancelText && modal.cancelText.length > 0"
            @click="cancel"
            :label="modal.cancelText"
            kind="ghost"
          />
          <SimpleButton @click="confirm" :label="modal.confirmText" kind="primary" />
        </div>

        <div
          class="actions prompt"
          v-if="
            modal.kind === 'builtin' &&
            (modal.type === 'prompt' || modal.type === 'multiline-prompt')
          "
        >
          <SimpleButton
            v-if="modal.cancelText && modal.cancelText.length > 0"
            @click="cancel"
            :label="modal.cancelText"
            kind="ghost"
          />
          <SimpleButton
            @click="confirm"
            :label="modal.confirmText"
            kind="primary"
            :disabled="isPromptConfirmButtonDisabled"
          />
        </div>

        <div class="actions component" v-if="modal.kind === 'component' && modal.footer">
          <component
            :is="modal.footer"
            v-bind="{
              ...(modal.footerProps ?? {}),
              ...modelBindings,
              close: modal.close,
              cancel: modal.cancel,
            }"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.container {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  width: 100dvw;
  height: 100dvh;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  pointer-events: auto;

  .dialog {
    position: relative;
    min-width: 70dvw;
    border: solid var(--stroke-width-md) var(--border-tertiary);
    border-radius: var(--numbers-12);
    background-color: var(--background-primary);

    .header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: var(--numbers-16);
      border-bottom: solid var(--stroke-width-md) var(--border-tertiary);

      .icon {
        display: inline-flex;
        width: var(--numbers-40);
        height: var(--numbers-40);
        align-items: center;
        justify-content: center;
        border-radius: var(--numbers-6);
        margin-right: var(--numbers-4);
        background-color: var(--background-notice-subtle);
        color: var(--content-brand-2);
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      padding: var(--numbers-16);
      gap: var(--numbers-16);

      &.component {
        padding: 0;
      }

      .prompt {
        display: flex;
        flex-direction: column;
        gap: var(--numbers-4);

        .text-input {
          background-color: var(--background-primary);
        }

        .prompt-error {
          color: var(--content-negative);
        }
      }

      .editor {
        display: flex;
        height: 15dvh;
        flex-direction: column;
        padding: var(--numbers-4);
        border-radius: var(--numbers-6);
        background-color: var(--background-secondary);
        gap: var(--numbers-4);

        .format-actions {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
        }

        .rich-text-editor {
          flex-grow: 1;
        }
      }

      .expandable-block {
        .expand-inner {
          display: grid;
          overflow: hidden;

          &.modal-expand-enter-active,
          &.modal-expand-leave-active {
            overflow: hidden;
            transition:
              grid-template-rows var(--animation-duration) var(--animation-easing),
              opacity var(--animation-duration) var(--animation-easing);
          }

          &.modal-expand-enter-from,
          &.modal-expand-leave-to {
            grid-template-rows: 0fr;
            opacity: 0;
          }

          &.modal-expand-enter-to,
          &.modal-expand-leave-from {
            grid-template-rows: 1fr;
            opacity: 1;
          }

          .expand-wrapper {
            display: flex;
            overflow: hidden;
            min-height: 0;
            flex-direction: column;
            gap: var(--numbers-8);
          }
        }
      }

      &.content-expandable {
        gap: 0;

        .expandable-block .expand-inner {
          margin-top: var(--numbers-16);
        }
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: var(--numbers-16);
      border-top: solid var(--stroke-width-md) var(--border-tertiary);
      gap: var(--numbers-16);

      .skore-button {
        flex-grow: 1;
      }

      &.expandable {
        .skore-button {
          .icon.right {
            transition: transform var(--animation-duration) var(--animation-easing);
          }

          &.toggle-expanded .icon.right {
            transform: rotate(180deg);
          }
        }
      }
    }

    &.image {
      width: auto;
      min-width: 0;
      max-width: 80dvw;

      .content {
        align-items: center;

        img {
          display: block;
          width: auto;
          max-width: 80dvw;
          height: auto;
          max-height: 80dvh;
          object-fit: contain;
        }

        .caption {
          margin: 0;
          color: var(--content-secondary);
          text-align: center;
        }
      }
    }

    &.danger {
      .header {
        .icon {
          background-color: var(--background-negative-subtle);
          color: var(--content-negative);
        }
      }

      .actions {
        .skore-button.primary:not(:disabled) {
          border: solid var(--stroke-width-md) var(--border-negative);
          background-color: var(--background-negative-subtle);
          color: var(--content-negative);
        }
      }
    }
  }

  &.modal-appear-enter-active,
  &.modal-appear-leave-active {
    transition: opacity var(--animation-duration) var(--animation-easing);

    & .dialog {
      transition: transform var(--animation-duration) var(--animation-easing);
    }
  }

  &.modal-appear-enter-from,
  &.modal-appear-leave-to {
    opacity: 0;

    & .dialog {
      transform: translateY(30px);
    }
  }

  @media screen and (width >=801px) {
    .dialog {
      min-width: 60dvw;
    }
  }

  @media screen and (width >=1025px) {
    .dialog {
      min-width: 50dvw;
    }
  }

  @media screen and (width >=1279px) {
    .dialog {
      min-width: 40dvw;
    }
  }
}
</style>
